
import { useEffect, useState, useRef } from 'react';
import { 
  isLovableEditor, 
  useSafeRendering, 
  isRecoveryMode, 
  resetRecoveryMode,
  checkEnvironmentDetectionConsistency,
  refreshEnvironmentDetection,
  storeEditorDetection,
  _DEBUG_forceEditorState,
  applyEditorDOMChanges
} from '@/utils/environment';

/**
 * Component that detects Lovable editor environment and makes necessary adjustments
 * Also provides automatic recovery from blank screens
 */
export function LovableEditorDetector() {
  const [initialized, setInitialized] = useState(false);
  const [blankDetected, setBlankDetected] = useState(false);
  const [detectedMode, setDetectedMode] = useState<string>('');
  const initRef = useRef<boolean>(false);
  
  // Main editor detection effect - run ONCE on mount, never again
  useEffect(() => {
    // Ensure this effect runs EXACTLY ONCE
    if (initRef.current) {
      console.log('[LovableEditorDetector] Already initialized, skipping');
      return;
    }
    
    initRef.current = true;
    
    console.log('[LovableEditorDetector] Initializing (first mount)...');
    
    // Mark initialization timestamp to help with race condition debugging
    try {
      window.__LOVABLE_EDITOR_INITIALIZED = Date.now();
      sessionStorage.setItem('lamulamu_editor_init_time', new Date().toISOString());
    } catch (e) {
      // Ignore storage errors
    }
    
    // Get the environment state (should already be initialized in main.tsx)
    const isEditor = isLovableEditor();
    setDetectedMode(isEditor ? 'Lovable Editor Mode' : 'Standard Mode');
    
    // Verify detection consistency early
    checkEnvironmentDetectionConsistency();
    
    // Reset recovery mode on successful component mount
    if (isRecoveryMode()) {
      console.log('[LovableEditorDetector] Resetting recovery mode as component mounted successfully');
      resetRecoveryMode();
    }
    
    if (isEditor) {
      console.log('[LovableEditorDetector] Running in Lovable Editor - applying optimizations');
      
      // Store the state again for redundancy
      storeEditorDetection(true);
      
      // Apply DOM changes for consistent CSS targeting
      applyEditorDOMChanges(true);
      
      // Disable animations in editor for better performance
      document.documentElement.style.setProperty('--animation-duration', '0s');
      document.documentElement.style.setProperty('--transition-duration', '0s');
      
      // Add editor-specific styles
      const style = document.createElement('style');
      style.innerHTML = `
        /* Disable animations in editor */
        .lovable-editor * {
          animation-duration: 0s !important;
          transition-duration: 0s !important;
          animation-delay: 0s !important;
          transition-delay: 0s !important;
        }
        
        /* Hide non-essential elements in editor */
        .lovable-editor .hide-in-editor {
          display: none !important;
        }
        
        /* Simplify layouts in editor */
        .lovable-editor .simplify-in-editor {
          overflow: visible !important;
          height: auto !important;
          max-height: none !important;
        }
      `;
      document.head.appendChild(style);
      
      // Force non-lazy loading in editor
      window.__FORCE_EAGER_LOADING = true;
      
      // Log editor detection for debugging
      console.log('[LovableEditorDetector] Lovable Editor environment detected and configured');
      
      // Store detection state in window for external access
      window.__LOVABLE_IS_EDITOR = true;
      
      // Listen for HMR updates to ensure consistent detection
      if (import.meta.hot) {
        // Store a timestamp for the last update
        const lastUpdateTime = Date.now();
        window.__LOVABLE_LAST_HMR_UPDATE = lastUpdateTime;
        
        import.meta.hot.on('vite:beforeUpdate', () => {
          console.log('[LovableEditorDetector] HMR update detected, preserving editor state...');
          document.body.classList.add('hmr-updating');
          
          // Store current state before HMR update
          try {
            const currentValue = isLovableEditor();
            sessionStorage.setItem('lamulamu_hmr_preserving', JSON.stringify({
              isEditor: currentValue,
              timestamp: Date.now()
            }));
          } catch (e) {
            // Ignore storage errors
          }
        });
        
        import.meta.hot.on('vite:afterUpdate', () => {
          console.log('[LovableEditorDetector] HMR update completed, verifying environment consistency...');
          document.body.classList.remove('hmr-updating');
          
          // Verify that editor detection state hasn't changed
          setTimeout(() => {
            try {
              const preserved = sessionStorage.getItem('lamulamu_hmr_preserving');
              if (preserved) {
                const data = JSON.parse(preserved);
                const currentValue = isLovableEditor();
                
                if (data.isEditor !== currentValue) {
                  console.warn('[LovableEditorDetector] Editor detection changed during HMR!', {
                    before: data.isEditor,
                    after: currentValue
                  });
                  
                  // Force the previous value
                  _DEBUG_forceEditorState(data.isEditor);
                  console.log('[LovableEditorDetector] Restored previous editor state');
                }
                
                // Clean up
                sessionStorage.removeItem('lamulamu_hmr_preserving');
              }
              
              // Update lastUpdateTime
              window.__LOVABLE_LAST_HMR_UPDATE = Date.now();
            } catch (e) {
              console.error('[LovableEditorDetector] Error during HMR consistency check:', e);
            }
          }, 100);
        });
      }
    } else {
      console.log('[LovableEditorDetector] Not running in Lovable Editor');
      // Store detection state in window for external access
      window.__LOVABLE_IS_EDITOR = false;
    }
    
    setInitialized(true);
    
    // Return cleanup function
    return () => {
      console.log('[LovableEditorDetector] Cleanup - this should not happen in normal operation');
    };
  }, []); // Empty dependency array ensures this runs exactly once
  
  // Blank screen detection and recovery - separate from main effect
  useEffect(() => {
    if (!isLovableEditor()) return;
    
    let timeoutId: number;
    
    const checkForBlankScreen = () => {
      // Check if React is properly rendered
      const appRoot = document.getElementById('root');
      const hasVisibleContent = appRoot && (
        appRoot.innerText.length > 10 || 
        appRoot.querySelectorAll('*').length > 5
      );
      
      if (!hasVisibleContent) {
        console.log('[LovableEditorDetector] Blank screen detected, preparing recovery...');
        setBlankDetected(true);
        
        // Wait a bit longer to confirm it's really blank
        timeoutId = window.setTimeout(() => {
          const appRoot = document.getElementById('root');
          const hasVisibleContent = appRoot && (
            appRoot.innerText.length > 10 || 
            appRoot.querySelectorAll('*').length > 5
          );
          
          if (!hasVisibleContent) {
            console.log('[LovableEditorDetector] Confirmed blank screen, triggering hard refresh');
            // Set recovery mode flag
            try {
              sessionStorage.setItem('lamulamu_recovery_mode', 'true');
              sessionStorage.setItem('lamulamu_last_recovery', new Date().toISOString());
              // Preserve the editor detection state during recovery
              const isEditor = isLovableEditor();
              sessionStorage.setItem('lamulamu_pre_recovery_editor_state', isEditor ? 'true' : 'false');
            } catch (e) {
              // Ignore storage errors
            }
            
            // Force hard reload
            window.location.reload();
          } else {
            console.log('[LovableEditorDetector] Content appeared after delay, canceling recovery');
            setBlankDetected(false);
          }
        }, 2000);
      }
    };
    
    // Run check after a short delay to allow normal rendering
    const initialCheckId = window.setTimeout(checkForBlankScreen, 1000);
    
    return () => {
      clearTimeout(timeoutId);
      clearTimeout(initialCheckId);
    };
  }, []);
  
  // If blank screen is detected, show minimal recovery UI
  if (blankDetected && isLovableEditor()) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: '#f0f8ff',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100000,
        padding: '20px',
        fontFamily: 'sans-serif'
      }}>
        <div style={{ maxWidth: '400px', textAlign: 'center' }}>
          <h2 style={{ color: '#0055aa', marginBottom: '15px' }}>LamuLamu Editor Recovery</h2>
          <p style={{ marginBottom: '20px', color: '#444' }}>
            Loading application content... If nothing appears within a few seconds, the page will automatically refresh.
          </p>
          <button 
            onClick={() => window.location.reload()} 
            style={{
              backgroundColor: '#0055aa',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Refresh Now
          </button>
        </div>
      </div>
    );
  }
  
  // This component doesn't render anything in normal operation
  return null;
}

// Additional types for global properties
declare global {
  interface Window {
    __FORCE_EAGER_LOADING?: boolean;
    __LOVABLE_IS_EDITOR?: boolean;
    __LOVABLE_LAST_HMR_UPDATE?: number;
    __LOVABLE_EDITOR_DETECTION?: boolean | null;
    __LOVABLE_EDITOR_INITIALIZED?: number;
    __LOVABLE_EDITOR_FORCED_TIME?: number;
  }
}
